import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Stack,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  FileDownload as FileDownloadIcon,
} from '@mui/icons-material';
import Layout from '../../components/layout/Layout';
import taxService from '../../services/taxService';
import { getCurrentUser } from '../../services/authService';
import PageTitle from '../../components/PageTitle';
import { useSnackbar } from 'notistack';
import { downloadXlsxFromRows } from '../../utils/inventoryListExport';

const TABLE_SX = {
  tableLayout: 'fixed',
  width: '100%',
  '& .MuiTableCell-root': {
    py: 0.65,
    px: 1.5,
    fontSize: '0.8125rem',
    borderBottom: '1px solid',
    borderColor: 'divider',
  },
  '& .MuiTableCell-head': {
    py: 0.85,
    fontWeight: 600,
    fontSize: '0.75rem',
    color: 'text.secondary',
    bgcolor: 'grey.50',
    whiteSpace: 'nowrap',
  },
  '& .MuiTableRow-root:hover': {
    bgcolor: 'action.hover',
  },
};

function supplyTypeLabel(supplyType) {
  if (supplyType === 'NIL_RATED') return 'GST 0%';
  if (supplyType === 'EXEMPT') return 'Exempt';
  if (supplyType === 'NON_GST') return 'None';
  return 'Standard';
}

/**
 * Tax Rates management page component
 * @returns {JSX.Element} Taxes page
 */
const TaxesPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const user = getCurrentUser();
  const companyId = user?.companyId || 1; // Fallback to 1 for development
  const taxNameInputRef = useRef(null);

  // Set page title on component mount
  useEffect(() => {
    document.title = "Tax Rates";
  }, []);

  // State management
  const [allTaxes, setAllTaxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTax, setSelectedTax] = useState(null);
  const [taxToDelete, setTaxToDelete] = useState(null);
  const [formValues, setFormValues] = useState({
    name: '',
    rate: '',
    description: '',
    status: 'ACTIVE',
    supplyType: 'STANDARD'
  });

  // Load all tax rates (API returns full list for company)
  const loadTaxes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await taxService.getAllTaxes({
        page: 0,
        size: 10000,
        sort: 'name',
        direction: 'ASC'
      });

      if (response && response.content) {
        setAllTaxes(response.content);
      } else {
        setAllTaxes(Array.isArray(response) ? response : []);
      }
    } catch (error) {
      console.error('Error loading tax rates:', error);
      window.alert('Failed to load tax rates: ' + (error.response?.data?.message || error.message || 'Unknown error'));
      setAllTaxes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadTaxes();
  }, [loadTaxes]);

  useEffect(() => {
    setPage(0);
  }, [searchQuery]);

  const normalizedSearch = searchQuery.trim().toLowerCase();

  const filteredTaxes = useMemo(() => {
    if (!normalizedSearch) return allTaxes;
    return allTaxes.filter((t) => {
      const supply = supplyTypeLabel(t.supplyType || 'STANDARD').toLowerCase();
      const status = (t.status === 'ACTIVE' ? 'active' : 'inactive');
      return (
        (t.name || '').toLowerCase().includes(normalizedSearch) ||
        (t.description || '').toLowerCase().includes(normalizedSearch) ||
        String(t.rate ?? '').includes(normalizedSearch) ||
        supply.includes(normalizedSearch) ||
        status.includes(normalizedSearch)
      );
    });
  }, [allTaxes, normalizedSearch]);

  const displayedTaxes = useMemo(
    () => filteredTaxes.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredTaxes, page, rowsPerPage]
  );

  const filteredCount = filteredTaxes.length;

  const handleExportTaxesExcel = useCallback(() => {
    const rows = filteredTaxes.map((t) => ({
      ID: t.id,
      Name: t.name || '',
      'Supply type': supplyTypeLabel(t.supplyType || 'STANDARD'),
      'Rate (%)': t.rate ?? '',
      Description: t.description || '',
      Status: t.status === 'ACTIVE' ? 'Active' : 'Inactive',
    }));
    const ok = downloadXlsxFromRows(
      `tax_rates_${new Date().toISOString().split('T')[0]}.xlsx`,
      'Tax rates',
      rows
    );
    if (!ok) {
      enqueueSnackbar('Nothing to export', { variant: 'info' });
    }
  }, [filteredTaxes, enqueueSnackbar]);

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Dialog handlers
  const handleOpenDialog = (tax = null) => {
    if (tax) {
      setSelectedTax(tax);
      setFormValues({
        name: tax.name,
        rate: tax.rate,
        description: tax.description || '',
        status: tax.status || 'ACTIVE',
        supplyType: tax.supplyType || 'STANDARD'
      });
    } else {
      setSelectedTax(null);
      setFormValues({
        name: '',
        rate: '',
        description: '',
        status: 'ACTIVE',
        supplyType: 'STANDARD'
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedTax(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'supplyType' && ['NIL_RATED', 'EXEMPT', 'NON_GST'].includes(value)) {
        next.rate = '0';
      }
      return next;
    });
  };

  // Form submission
  const handleSubmit = async () => {
    try {
      // Convert rate to decimal for storage
      const taxData = {
        name: formValues.name,
        rate: parseFloat(formValues.rate),
        description: formValues.description,
        status: formValues.status,
        supplyType: formValues.supplyType || 'STANDARD',
        companyId
      };

      if (selectedTax) {
        // Update existing tax
        await taxService.updateTax(selectedTax.id, taxData);
        enqueueSnackbar('Tax rate updated successfully', { variant: 'success' });
      } else {
        // Create new tax
        await taxService.createTax(taxData);
        enqueueSnackbar('Tax rate created successfully', { variant: 'success' });
      }

      // Close the dialog and refresh data
      handleCloseDialog();
      loadTaxes();
    } catch (error) {
      console.error('Error saving tax rate:', error);
      window.alert(
        `Failed to ${selectedTax ? 'update' : 'create'} tax rate: ${error.response?.data?.message || error.message}`
      );
    }
  };

  // Validation logic
  const isFormValid = Boolean(
    formValues.name &&
    (formValues.rate !== '' && formValues.rate !== null && formValues.rate !== undefined) &&
    !Number.isNaN(parseFloat(formValues.rate))
  );

  // Delete handlers
  const handleOpenDeleteDialog = (tax) => {
    setTaxToDelete(tax);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setTaxToDelete(null);
  };

  const handleDeleteTax = async () => {
    try {
      await taxService.deleteTax(taxToDelete.id);
      enqueueSnackbar('Tax rate deleted successfully', { variant: 'success' });
      handleCloseDeleteDialog();
      loadTaxes();
    } catch (error) {
      console.error('Error deleting tax rate:', error);
      window.alert(
        `Failed to delete tax rate: ${error.response?.data?.message || error.message}`
      );
    }
  };

  return (
    <Layout>
      <PageTitle title="Tax Rates" />
      <Box sx={{ width: '100%', px: { xs: 1.5, sm: 2, md: 3 }, py: 1.5, bgcolor: '#fafbfc', minHeight: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5, flexWrap: 'wrap', gap: 1.5 }}>
          <Typography variant="h6" fontWeight={700} color="text.primary">
            Tax Rates
          </Typography>

          <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" sx={{ maxWidth: { xs: '100%', sm: 'auto' } }}>
            <TextField
              size="small"
              placeholder="Search name, rate, type, status…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoComplete="off"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: { xs: '100%', sm: 260 }, flex: { sm: 1 } }}
            />
            <Button variant="outlined" color="primary" size="small" startIcon={<FileDownloadIcon />} onClick={handleExportTaxesExcel}>
              Export Excel
            </Button>
            <Button variant="contained" color="primary" size="small" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
              Add Tax Rate
            </Button>
          </Stack>
        </Box>

        <Box sx={{ width: '100%', maxWidth: '95%', mx: 'auto' }}>
          <Paper elevation={0} variant="outlined" sx={{
            p: 0,
            borderRadius: 1,
            overflow: 'hidden',
          }}>
            {loading && <LinearProgress />}
            <TableContainer>
              <Table size="small" stickyHeader sx={TABLE_SX}>
                <TableHead>
                  <TableRow>
                    <TableCell width="22%">Name</TableCell>
                    <TableCell width="16%">Supply type</TableCell>
                    <TableCell width="10%" align="right">Rate</TableCell>
                    <TableCell width="30%">Description</TableCell>
                    <TableCell width="12%">Status</TableCell>
                    <TableCell align="center" width="10%">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading && allTaxes.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                        Loading…
                      </TableCell>
                    </TableRow>
                  ) : displayedTaxes.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                        No tax rates found
                      </TableCell>
                    </TableRow>
                  ) : (
                    displayedTaxes.map((tax) => (
                      <TableRow key={tax.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600} noWrap title={tax.name}>
                            {tax.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            sx={{ height: 24, '& .MuiChip-label': { px: 1, fontSize: '0.75rem' } }}
                            label={supplyTypeLabel(tax.supplyType)}
                            color={
                              tax.supplyType === 'NON_GST'
                                ? 'default'
                                : tax.supplyType === 'EXEMPT'
                                  ? 'warning'
                                  : tax.supplyType === 'NIL_RATED'
                                    ? 'info'
                                    : 'primary'
                            }
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {tax.rate}%
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary" noWrap title={tax.description || ''}>
                            {tax.description?.trim() ? tax.description : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={tax.status === 'ACTIVE' ? 'Active' : 'Inactive'}
                            color={tax.status === 'ACTIVE' ? 'success' : 'default'}
                            size="small"
                            sx={{ height: 24, '& .MuiChip-label': { px: 1, fontSize: '0.75rem' } }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Edit">
                            <IconButton size="small" color="info" onClick={() => handleOpenDialog(tax)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => handleOpenDeleteDialog(tax)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={filteredCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              size="small"
            />
          </Paper>
        </Box>
      </Box>

      {/* Add/Edit Tax Rate Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        TransitionProps={{
          onEntered: () => {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                taxNameInputRef.current?.focus({ preventScroll: true });
              });
            });
          },
        }}
      >
        <DialogTitle>
          {selectedTax ? 'Edit Tax Rate' : 'Add New Tax Rate'}
        </DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 2 }}>
            <TextField
              inputRef={taxNameInputRef}
              fullWidth
              label="Tax Name"
              name="name"
              value={formValues.name}
              onChange={handleInputChange}
              margin="normal"
              required
              helperText="e.g., VAT, GST, Sales Tax"
              autoComplete="off"
            />

            <FormControl fullWidth margin="normal">
              <InputLabel id="supply-type-label">Supply type</InputLabel>
              <Select
                labelId="supply-type-label"
                name="supplyType"
                value={formValues.supplyType || 'STANDARD'}
                onChange={handleInputChange}
                label="Supply type"
              >
                <MenuItem value="STANDARD">Standard (GST / IGST)</MenuItem>
                <MenuItem value="NIL_RATED">GST 0% (nil-rated)</MenuItem>
                <MenuItem value="EXEMPT">Exempt</MenuItem>
                <MenuItem value="NON_GST">None</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Rate (%)"
              name="rate"
              type="number"
              inputProps={{ min: 0, max: 100, step: 0.01 }}
              value={formValues.rate}
              onChange={handleInputChange}
              margin="normal"
              required
              disabled={['NIL_RATED', 'EXEMPT', 'NON_GST'].includes(formValues.supplyType)}
              helperText={
                ['NIL_RATED', 'EXEMPT', 'NON_GST'].includes(formValues.supplyType)
                  ? 'Must be 0% for this type'
                  : 'Tax rate percentage (e.g., 18 for 18%)'
              }
              autoComplete="off"
            />

            <FormControl fullWidth margin="normal">
              <InputLabel id="status-label">Status</InputLabel>
              <Select
                labelId="status-label"
                name="status"
                value={formValues.status}
                onChange={handleInputChange}
                label="Status"
              >
                <MenuItem value="ACTIVE">Active</MenuItem>
                <MenuItem value="INACTIVE">Inactive</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Description"
              name="description"
              value={formValues.description}
              onChange={handleInputChange}
              margin="normal"
              multiline
              rows={3}
              helperText="Optional description of the tax rate"
              autoComplete="off"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={!isFormValid}
          >
            {selectedTax ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the tax rate <strong>{taxToDelete?.name}</strong>?
            This action cannot be undone.
          </Typography>
          <Typography variant="caption" color="error" sx={{ mt: 2, display: 'block' }}>
            Note: Tax rates that are in use by products cannot be deleted. You must reassign products to a different tax rate first.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button
            onClick={handleDeleteTax}
            variant="contained"
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default TaxesPage; 
